import { useCallback } from 'react'
import {
  Table,
  Text,
  Box,
  Card,
  LoadingOverlay,
  Skeleton,
  OptionsFilter,
  ComboboxItem,
  Group,
  Tooltip,
} from '@mantine/core'
import uniq from 'lodash-es/uniq'
import { TableActions } from './TableActions'
import { useCsvDownload } from '@/hooks/useCsvDownload'
import { TableColumn, GenericTableProps } from './types'
import { useTranslation } from 'react-i18next'

// Re-export TableColumn for backward compatibility
export type { TableColumn } from './types'

export function GenericTable<T>({
  data,
  columns,
  onColumnsChange,
  isLoading = false,
  error = null,
  itemsPerPage = 20,
  currentPage,
  onPageChange,
  filterField,
  onFilterFieldChange,
  search,
  onSearchChange,
  hideConfig = false,
  configTitle,
  getRowKey,
  emptyMessage = 'No data available',
  paginatedData,
  filteredData,
  isDebouncing,
  stickyHeader = false,
  verticalSpacing = 'sm',
  hideCsvDownload = false,
  csvFileName = 'data.csv',
  sortDirection,
  onSortDirectionChange,
  sortField,
  onSortFieldChange,
  restrict,
}: GenericTableProps<T>) {
  const { t } = useTranslation('common')
  const resolvedConfigTitle = configTitle ?? t('configureTableView')

  const renderCell = useCallback((item: T, column: TableColumn<T>) => {
    // Use custom render function if provided
    if (column.render) {
      return column.render(item, column.key)
    }

    // Default rendering
    const value = item[column.key]
    const textAlign = column.align || 'left'

    if (value === null || value === undefined) {
      return (
        <Text c="dimmed" style={{ textAlign }}>
          N/A
        </Text>
      )
    }

    if (typeof value === 'string') {
      return <Text style={{ textAlign }}>{value}</Text>
    }

    if (typeof value === 'number') {
      return <Text style={{ textAlign, width: '100%' }}>{value}</Text>
    }

    if (typeof value === 'boolean') {
      return <Text style={{ textAlign }}>{value ? 'Yes' : 'No'}</Text>
    }

    if (column.type === 'date') {
      return <Text style={{ textAlign }}>{}</Text>
    }

    return <Text style={{ textAlign }}>{String(value)}</Text>
  }, [])

  // Get visible columns for rendering
  const visibleColumns = columns.filter(col => col.visible)

  // Use provided paginatedData if available, otherwise fall back to data
  const displayData = paginatedData || data

  // Get unique values for filter dropdown
  const getFilterOptions = () => {
    if (!filterField) return []

    return uniq(
      data
        .map(item => item[filterField])
        .filter(value => value !== null && value !== undefined)
        .map(String)
    )
  }

  const { downloadCsv } = useCsvDownload({
    data,
    columns,
    fileName: csvFileName,
  })

  if (error) {
    return (
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Text color="red">Error loading data: {error.message}</Text>
      </Card>
    )
  }

  const optionsFilter: OptionsFilter = ({ options, search }) => {
    const filtered = (options as ComboboxItem[]).filter(option =>
      option.label
        .toLocaleLowerCase()
        .trim()
        .includes(search?.toLocaleLowerCase().trim() || '')
    )

    filtered.sort((a, b) => a.label.localeCompare(b.label))
    return filtered
  }

  const filterableColumns = columns
    .filter(col => col.sortable !== false && col.visible)
    .map(col => ({
      label: col.label,
      value: String(col.key),
    }))
    .sort((a, b) => a.label.localeCompare(b.label))

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <TableActions
        columns={columns}
        onColumnsChange={onColumnsChange}
        configTitle={resolvedConfigTitle}
        hideConfig={hideConfig}
        hideCsvDownload={hideCsvDownload}
        csvFileName={csvFileName}
        data={data}
        onDownloadCsv={downloadCsv}
        filterableColumns={filterableColumns}
        onFilterFieldChange={onFilterFieldChange}
        onSearchChange={onSearchChange}
        search={search}
        filterField={filterField}
        getFilterOptions={getFilterOptions}
        optionsFilter={optionsFilter}
        filteredData={filteredData}
        itemsPerPage={itemsPerPage}
        currentPage={currentPage}
        onPageChange={onPageChange}
        sortDirection={sortDirection}
        onSortDirectionChange={onSortDirectionChange}
        sortField={sortField}
        onSortFieldChange={onSortFieldChange}
        restrict={restrict}
      />
      <Box
        pos="relative"
        mt="lg"
        style={{
          position: 'relative',
          zIndex: 1,
          // Only add scroll when not paginating
          ...(filteredData && !stickyHeader
            ? {}
            : {
                overflow: 'auto',
                maxHeight: '75vh',
              }),
        }}
      >
        <LoadingOverlay visible={isLoading} />
        {isDebouncing ? (
          <Group justify="flex-start" mt="md" style={{ minHeight: '200px' }}>
            <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
              Searching...
            </Text>
            <Skeleton height={20} radius="xl" />
            <Skeleton height={20} mt={6} radius="xl" />
            <Skeleton height={20} mt={6} width="70%" radius="xl" />
            <Skeleton height={20} mt={6} width="60%" radius="xl" />
            <Skeleton height={20} mt={6} width="50%" radius="xl" />
            <Skeleton height={20} mt={6} width="40%" radius="xl" />
            <Skeleton height={20} mt={6} width="30%" radius="xl" />
            <Skeleton height={20} mt={6} width="20%" radius="xl" />
            <Skeleton height={20} mt={6} width="10%" radius="xl" />
            <Skeleton height={16} mt={6} width="5%" radius="xl" />
          </Group>
        ) : (
          <div
            style={{
              position: 'relative',
            }}
          >
            <Table
              highlightOnHover
              verticalSpacing={verticalSpacing}
              horizontalSpacing={verticalSpacing}
              stickyHeader
              stickyHeaderOffset={0}
              style={{
                // Ensure table has proper positioning context
                position: 'relative',
                zIndex: 2,
              }}
            >
              <Table.Thead>
                <Table.Tr>
                  {visibleColumns.map(column => (
                    <Table.Th
                      key={String(column.key)}
                      style={{ textAlign: column.align || 'left', color: 'light-dark(inherit, #c9c9c9)' }}
                    >
                      {column.tooltip ? (
                        <Tooltip label={column.tooltip} multiline maw={300} withArrow>
                          <span
                            style={{
                              borderBottom: '1px dotted light-dark(var(--mantine-color-dimmed), #c9c9c9)',
                              cursor: 'help',
                            }}
                          >
                            {column.label}
                          </span>
                        </Tooltip>
                      ) : (
                        column.label
                      )}
                    </Table.Th>
                  ))}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {displayData.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={visibleColumns.length}>
                      <Text ta="center" c="dimmed" py="xl">
                        {emptyMessage}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  displayData.map(item => (
                    <Table.Tr key={getRowKey(item)}>
                      {visibleColumns.map(column => (
                        <Table.Td key={String(column.key)} style={{ textAlign: column.align || 'left' }}>
                          {renderCell(item, column)}
                        </Table.Td>
                      ))}
                    </Table.Tr>
                  ))
                )}
              </Table.Tbody>
            </Table>
          </div>
        )}
      </Box>
    </Card>
  )
}
